import { useOptionalUser } from "@/components/UserProvider";
import { useEffect, useState } from "react";
import { useInfiniteQuery, useQueries } from "@tanstack/react-query";
import { BookOpen, Paperclip, Plus, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useUploadPersonalFile } from "@/lib/data/react-query/file-library";
import { automationFilesQueryOptions } from "@/lib/data/react-query/automations";
import { attachmentContentQueryOptions } from "@/lib/data/react-query/chat-attachments";
import { automationError } from "@/components/features/automations/automationError";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/shadcn/DropdownMenu/DropdownMenu";
import { ChatAttachmentPreview } from "./ChatAttachmentPreview";
import { ChatSkillPicker } from "./ChatSkillPicker";
const MAX_ATTACHMENTS = 50;

export type ChatAttachment = {
  id: string;
  name: string;
  ready: boolean;
  localFile?: File;
  processingId?: string;
};
export function ChatAttachments({
  attachments,
  onChange,
  onBusyChange,
  disabled,
  onSelectSkill,
}: {
  attachments: ChatAttachment[];
  onChange: (next: ChatAttachment[]) => void;
  onBusyChange: (busy: boolean) => void;
  disabled?: boolean;
  onSelectSkill?: (nativeName: string) => void;
}) {
  const user = useOptionalUser();
  const canUpload = Boolean(user);
  const [open, setOpen] = useState(false);
  const [skillsOpen, setSkillsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [error, setError] = useState<string>();
  const upload = useUploadPersonalFile();
  const files = useInfiniteQuery({
    ...automationFilesQueryOptions(search, true),
    enabled: open,
  });
  const pending = attachments.filter((file) => !file.ready);
  const statuses = useQueries({
    queries: pending.map((file) => attachmentContentQueryOptions(file.id)),
  });
  const waiting = upload.isPending;
  useEffect(() => {
    onBusyChange(waiting);
  }, [waiting, onBusyChange]);
  useEffect(() => {
    const completed = new Map(
      statuses.flatMap((result) =>
        result.isSuccess &&
        result.isFetchedAfterMount &&
        !result.isFetching &&
        result.data.processingId
          ? [[result.data.id, result.data.processingId] as const]
          : [],
      ),
    );
    if (completed.size)
      onChange(
        attachments.map((file) =>
          completed.has(file.id)
            ? { ...file, ready: true, processingId: completed.get(file.id) }
            : file,
        ),
      );
  }, [statuses, attachments, onChange]);
  const failed = statuses.some(
    (result) => result.isError || result.data?.status === "failed",
  );
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Add to message"
            className="absolute right-14 bottom-3 size-8 rounded-full"
            disabled={disabled || upload.isPending}
          >
            <Plus className="size-5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" side="top" className="min-w-48">
          <DropdownMenuItem
            disabled={attachments.length >= MAX_ATTACHMENTS}
            onSelect={() => {
              setOpen(true);
            }}
          >
            <Paperclip className="size-4" />
            Attach file
          </DropdownMenuItem>
          {onSelectSkill && (
            <DropdownMenuItem
              onSelect={() => {
                setSkillsOpen(true);
              }}
            >
              <BookOpen className="size-4" />
              Use a skill
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {onSelectSkill && skillsOpen && (
        <ChatSkillPicker
          open={skillsOpen}
          onOpenChange={setSkillsOpen}
          onSelect={onSelectSkill}
        />
      )}
      {attachments.length > 0 && (
        <div className="mb-3 flex gap-2 overflow-x-auto pb-1">
          {attachments.map((file) => (
            <ChatAttachmentPreview
              key={file.id}
              file={{
                id: file.id,
                fileName: file.name,
                processingId: file.processingId,
              }}
              localFile={file.localFile}
              ready={file.ready}
              onRemove={
                disabled || upload.isPending
                  ? undefined
                  : () => {
                      onChange(
                        attachments.filter((item) => item.id !== file.id),
                      );
                    }
              }
            />
          ))}
        </div>
      )}
      {failed && (
        <p role="alert" className="text-destructive mb-2 text-sm">
          A file could not be processed automatically. You can still send it and
          ask LawWorks to read the original.
        </p>
      )}
      {error && !open && (
        <p role="alert" className="text-destructive mb-2 text-sm">
          {error}
        </p>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-auto sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Attach a file</DialogTitle>
            <DialogDescription>
              Uploads are saved privately. You can send once uploading finishes.
              Processing continues independently.
            </DialogDescription>
          </DialogHeader>
          {error && (
            <p role="alert" className="text-destructive text-sm">
              {error}
            </p>
          )}
          {canUpload && (
            <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed p-4 text-sm">
              <Upload className="size-4" />
              {upload.isPending ? "Uploading…" : "Upload a file"}
              <input
                aria-label="Upload a file"
                className="sr-only"
                type="file"
                accept=".pdf,.docx,.png,.jpg,.jpeg,.webp,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg,image/webp"
                disabled={
                  upload.isPending || attachments.length >= MAX_ATTACHMENTS
                }
                onChange={async (event) => {
                  const file = event.target.files?.[0];
                  event.target.value = "";
                  if (!file) return;
                  const mime = file.type.toLowerCase();
                  const supported =
                    (/\.(png|jpe?g|webp)$/i.test(file.name) &&
                      (!mime ||
                        ["image/png", "image/jpeg", "image/webp"].includes(
                          mime,
                        ))) ||
                    (/\.pdf$/i.test(file.name) &&
                      (!mime || mime === "application/pdf")) ||
                    (/\.docx$/i.test(file.name) &&
                      (!mime ||
                        mime ===
                          "application/vnd.openxmlformats-officedocument.wordprocessingml.document"));
                  if (!supported) {
                    setError("Choose a PDF, DOCX, PNG, JPEG, or WebP file.");
                    return;
                  }
                  setError(undefined);
                  try {
                    const response = await upload.mutateAsync(file);
                    const contentId = response.fileContentId;
                    if (!contentId)
                      throw new Error(
                        "The upload was saved, but its processing reference was not returned. Please try uploading it again.",
                      );
                    onChange([
                      ...attachments.filter((item) => item.id !== contentId),
                      {
                        id: contentId,
                        name: response.name,
                        ready: false,
                        localFile: file,
                      },
                    ]);
                    setOpen(false);
                  } catch (cause) {
                    setError(
                      cause instanceof Error && !("isAxiosError" in cause)
                        ? cause.message
                        : automationError(cause),
                    );
                  }
                }}
              />
            </label>
          )}
          <p className="text-muted-foreground text-sm">
            Supported uploads: PDF, DOCX, PNG, JPEG, and WebP.
          </p>
          <Input
            aria-label="Find an existing file"
            placeholder="Find a file…"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
            }}
            maxLength={200}
          />
          {files.isPending && <p role="status">Loading files…</p>}
          {files.error && <p role="alert">{automationError(files.error)}</p>}
          <div className="max-h-72 space-y-2 overflow-auto">
            {files.data?.pages
              .flatMap((page) => page.data)
              .map((file) => (
                <button
                  key={file.id}
                  type="button"
                  disabled={
                    upload.isPending ||
                    attachments.some((item) => item.id === file.id) ||
                    attachments.length >= MAX_ATTACHMENTS
                  }
                  className="hover:bg-muted block w-full rounded-lg border p-3 text-left text-sm disabled:opacity-50"
                  onClick={() => {
                    onChange([
                      ...attachments,
                      {
                        id: file.id,
                        name: file.fileName,
                        ready: Boolean(file.processingId),
                        processingId: file.processingId ?? undefined,
                      },
                    ]);
                    setOpen(false);
                  }}
                >
                  <span className="block truncate font-medium">
                    {file.fileName}
                  </span>
                  <span className="text-muted-foreground text-xs">
                    {file.sourceKind === "firm" ? "Firm file" : "Case file"}
                  </span>
                </button>
              ))}
          </div>
          {files.hasNextPage && (
            <Button
              variant="outline"
              onClick={() => void files.fetchNextPage()}
              disabled={files.isFetchingNextPage}
            >
              More files
            </Button>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
